import { Link } from 'gatsby'
import React, { useEffect, useState } from 'react'

const CONSENT_STORAGE_KEY = 'cookie_consent'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

const clearGoogleAnalyticsCookies = () => {
  if (typeof document === 'undefined') return
  const cookies = document.cookie.split(';')
  const hostname = window.location.hostname
  const domainParts = hostname.split('.')
  const rootDomain =
    domainParts.length >= 2 ? `.${domainParts.slice(-2).join('.')}` : hostname

  for (const cookie of cookies) {
    const cookieName = cookie.split('=')[0].trim()
    if (cookieName === '_ga' || cookieName.startsWith('_ga_') || cookieName === '_gid' || cookieName === '_gat') {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${hostname};`
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${rootDomain};`
    }
  }
}

const CookieBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const savedConsent = window.localStorage.getItem(CONSENT_STORAGE_KEY)
    if (savedConsent !== 'granted' && savedConsent !== 'denied') {
      setIsVisible(true)
    }

    const handleCheckHash = () => {
      if (window.location.hash === '#cookie-preferences') {
        setIsVisible(true)
        window.history.replaceState(
          window.history.state,
          '',
          window.location.pathname + window.location.search
        )
      }
    }

    handleCheckHash()
    window.addEventListener('hashchange', handleCheckHash)
    return () => window.removeEventListener('hashchange', handleCheckHash)
  }, [])

  const updateConsent = (status: 'granted' | 'denied') => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, status)

      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          ['analytics_storage']: status,
        })
      }

      if (status === 'denied') {
        clearGoogleAnalyticsCookies()
      }
    }
    setIsVisible(false)
  }

  if (!isVisible) {
    return null
  }

  return (
    <div className="cookie-banner" role="region" aria-label="Cookie consent">
      <div className="cookie-banner__content">
        <p className="cookie-banner__text">
          This site uses Google Analytics cookies to understand how visitors read and interact with articles. See our{' '}
          <Link to="/privacy-policy">Privacy Policy</Link> for details.
        </p>
        <div className="cookie-banner__actions">
          <button
            type="button"
            className="cookie-banner__btn cookie-banner__btn--reject"
            onClick={() => updateConsent('denied')}
          >
            Reject
          </button>
          <button
            type="button"
            className="cookie-banner__btn cookie-banner__btn--accept"
            onClick={() => updateConsent('granted')}
          >
            Accept
          </button>
        </div>
      </div>

      <style jsx>{`
        .cookie-banner {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          background-color: var(--background-color-alt);
          border-top: 1px solid var(--line-color);
          box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.12);
          padding: 14px 20px;
        }
        .cookie-banner__content {
          max-width: 1140px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }
        .cookie-banner__text {
          font-size: 0.9rem;
          line-height: 1.4;
          color: var(--text-color-primary);
          margin: 0;
          flex: 1 1 300px;
        }
        .cookie-banner__actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
        }
        .cookie-banner__btn {
          font-family: inherit;
          font-size: 0.875rem;
          font-weight: 600;
          padding: 8px 16px;
          border-radius: 5px;
          cursor: pointer;
          transition: opacity 0.2s ease, transform 0.1s ease;
        }
        .cookie-banner__btn:hover {
          opacity: 0.9;
        }
        .cookie-banner__btn--accept {
          background-color: var(--color-brand-primary);
          color: var(--text-color-primary-inverse);
          border: 1px solid var(--color-brand-primary);
        }
        .cookie-banner__btn--reject {
          background-color: transparent;
          color: var(--text-color-primary);
          border: 1px solid var(--line-color);
        }
      `}</style>
    </div>
  )
}

export default CookieBanner
